import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { stringify } from "query-string";

import TeamPopupComponent from "../TeamPopup";
import { ShareWithFriendsModal } from "../ShareWithFriendsModal";
import FanSharingPromptPopup from "../FanSharingPromptPopup";
import GamedayStoryPromptPopup from "../GamedayStoryPromptPopup";
import {
  executeNativeShare,
  getFanSharingPromptText,
  getShareUrl,
} from "../MomentoDownloadButton";
import { StorageServiceInstance } from "../../services/storage";
import { AppServiceInstance } from "../../services/app";

function buildTeamPopupShareGameUrl(): string {
  const team = StorageServiceInstance.selectedGameData?.team1;
  const originBase = getShareUrl(team?.hostname).replace(/\/$/, "");
  const qs = stringify(
    {
      game: StorageServiceInstance.selectedGame || undefined,
      section: StorageServiceInstance.selectedSection || undefined,
      row: StorageServiceInstance.selectedRow || undefined,
      seat: StorageServiceInstance.selectedSeat || undefined,
      suite: StorageServiceInstance.selectedSuite || undefined,
    },
    { skipNull: true },
  );
  return qs ? `${originBase}/?${qs}` : `${originBase}/`;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Global host for operator-configured team pop-ups (MobX-driven from {@link StorageServiceInstance.pendingTeamPopup}).
 */
const TeamPopupLayer = observer((): JSX.Element | null => {
  const pending = StorageServiceInstance.pendingTeamPopup;
  const pendingKeepsake = StorageServiceInstance.pendingKeepsakeDownloadPopup;
  const pendingShare = StorageServiceInstance.pendingFanSharingPrompt;
  const pendingStory = StorageServiceInstance.pendingGamedayStoryPrompt;
  const showSharingPrompt = pendingShare && !pendingKeepsake && !pending;
  const showStoryPrompt = pendingStory && !pendingKeepsake && !pending && !pendingShare;
  const [shareFriendsOpen, setShareFriendsOpen] = useState(false);
  const [shareFriendsGameId, setShareFriendsGameId] = useState<string | null>(null);
  const [shareFriendsGameUrl, setShareFriendsGameUrl] = useState("");

  useEffect(() => {
    if (showSharingPrompt) {
      StorageServiceInstance.markFanSharingPromptShown();
    }
  }, [showSharingPrompt]);

  useEffect(() => {
    if (showStoryPrompt) {
      StorageServiceInstance.markGamedayStoryPromptShown();
    }
  }, [showStoryPrompt]);

  const handlePrimaryAction = useCallback(() => {
    const snap = StorageServiceInstance.pendingTeamPopup;
    if (!snap) return;
    const { clickAction, customUrl } = snap;
    StorageServiceInstance.clearPendingTeamPopup();
    if (clickAction === "custom_url" && customUrl?.trim()) {
      window.open(customUrl.trim(), "_blank", "noopener,noreferrer");
      return;
    }
    if (clickAction === "feedback") {
      AppServiceInstance.openSupportFeedback("team_popup");
      return;
    }
    if (clickAction === "share_with_friends") {
      const gid = StorageServiceInstance.selectedGameData?._id;
      if (!gid) {
        message.warning("Select a game first, then try again.");
        return;
      }
      setShareFriendsGameId(gid);
      setShareFriendsGameUrl(buildTeamPopupShareGameUrl());
      setShareFriendsOpen(true);
      return;
    }
    if (clickAction === "direct_to_purchase") {
      const run = StorageServiceInstance.teamPopupDirectPurchaseHandler;
      if (run) {
        run().catch(() => {
          message.error("Purchase could not be started. Try again from My Moments.");
        });
      } else {
        message.info("Open My Moments to use purchase from this pop-up.");
      }
    }
  }, []);

  const handleShareMyPhoto = useCallback(() => {
    const prepared = StorageServiceInstance.fanSharingPromptPrepared;
    const hostname = StorageServiceInstance.selectedGameData?.team1?.hostname;
    const text = getFanSharingPromptText(hostname);
    const url = getShareUrl(hostname);
    if (prepared) {
      executeNativeShare({
        finalImageUrl: prepared.finalImageUrl,
        actualMimeType: prepared.actualMimeType,
        uniqueFilename: prepared.uniqueFilename,
      }, text, "native", url);
    } else if (navigator.share) {
      navigator.share({ text, url, title: "Momento" }).catch(() => undefined);
    }
    StorageServiceInstance.dismissFanSharingPrompt();
  }, []);

  const handleCopyLink = useCallback(async () => {
    const url = getShareUrl(StorageServiceInstance.selectedGameData?.team1?.hostname);
    const copied = await copyTextToClipboard(url);
    if (copied) {
      message.success("Link copied");
    } else {
      message.error("Could not copy link");
    }
  }, []);

  const handleSharingLater = useCallback(() => {
    StorageServiceInstance.dismissFanSharingPrompt();
  }, []);

  const handleStoryLater = useCallback(() => {
    StorageServiceInstance.dismissGamedayStoryPrompt();
  }, []);

  const accent = StorageServiceInstance.team1Color?.trim() || undefined;
  const storyDefaultName = [
    StorageServiceInstance.selectedFirstName,
    StorageServiceInstance.selectedLastName,
  ].filter(Boolean).join(" ").trim();
  const storyDefaultEmail = (StorageServiceInstance.selectedEmail || "").trim();

  return (
    <>
      {pending ? (
        <TeamPopupComponent
          src={pending.imageUrl}
          popup={pending}
          onCloseModal={() => StorageServiceInstance.clearPendingTeamPopup()}
          onTapImage={handlePrimaryAction}
          requireExplicitDismiss
        />
      ) : null}
      {showSharingPrompt ? (
        <FanSharingPromptPopup
          teamColor={accent}
          copy={StorageServiceInstance.selectedGameData?.team1?.downloadPromptCopy}
          imageUrl={StorageServiceInstance.selectedGameData?.team1?.fanSharingPromptImage}
          onShare={handleShareMyPhoto}
          onCopyLink={handleCopyLink}
          onLater={handleSharingLater}
        />
      ) : null}
      {showStoryPrompt ? (
        <GamedayStoryPromptPopup
          teamColor={accent}
          copy={StorageServiceInstance.selectedGameData?.team1?.downloadPromptCopy}
          imageUrl={StorageServiceInstance.selectedGameData?.team1?.gamedayStoryPromptImage}
          defaultName={storyDefaultName}
          defaultEmail={storyDefaultEmail}
          onDismiss={handleStoryLater}
        />
      ) : null}
      <ShareWithFriendsModal
        open={shareFriendsOpen}
        onClose={() => setShareFriendsOpen(false)}
        gameId={shareFriendsGameId}
        gameUrl={shareFriendsGameUrl}
        accentColor={accent}
        profileId={StorageServiceInstance.selectedCustomerId || null}
      />
    </>
  );
});

export default TeamPopupLayer;
